import { useState } from "react";
import { toast } from "sonner";
import {
  Plus,
  Search,
  ShieldOff,
  Copy,
  QrCode,
  Check,
  AlertTriangle,
} from "lucide-react";
import {
  useCreateVpnProfile,
  useEmployees,
  useRevokeVpnProfile,
  useVpnProfileDetail,
  useVpnProfiles,
} from "@/api/queries";
import type {
  EmployeeRead,
  VpnProfileRead,
  VpnProfileWithSubscription,
} from "@/api/types";
import { Button } from "@/components/ui/button";
import { Card, CardBody } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Table, THead, TBody, TR, TH, TD } from "@/components/ui/table";

const SIGNARIS_AUTH_URL =
  import.meta.env.VITE_SIGNARIS_AUTH_URL || "https://auth.signaris.ru";

const STATUS_LABEL: Record<string, { tone: "success" | "warning" | "danger" | "neutral"; text: string }> = {
  active: { tone: "success", text: "Активен" },
  pending: { tone: "warning", text: "Ожидает" },
  revoked: { tone: "danger", text: "Отозван" },
};

export function VpnProfilesPage() {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [createOpen, setCreateOpen] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<VpnProfileRead | null>(null);
  const [detailId, setDetailId] = useState<string | null>(null);

  const list = useVpnProfiles({
    page,
    page_size: 50,
    status_filter: statusFilter || undefined,
  });

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between gap-6">
        <div className="min-w-0">
          <span className="label">Управление</span>
          <h1 className="font-display text-3xl font-bold mt-2">VPN-профили</h1>
          <p className="text-fg-muted mt-1 text-sm">
            Один сотрудник может иметь несколько профилей. После revoke создайте
            новый — старый останется в audit log.
          </p>
        </div>
        <Button
          onClick={() => setCreateOpen(true)}
          className="whitespace-nowrap shrink-0"
        >
          <Plus size={14} /> Выпустить VPN-конфиг
        </Button>
      </header>

      <div className="flex gap-2 items-center">
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
          className="h-9 rounded-md border border-border bg-transparent px-3 text-sm"
        >
          <option value="">Все статусы</option>
          <option value="active">Активные</option>
          <option value="pending">Ожидающие</option>
          <option value="revoked">Отозванные</option>
        </select>
        <div className="text-sm text-fg-subtle">
          {list.data ? `${list.data.total} профилей` : "…"}
        </div>
      </div>

      <Card>
        <CardBody>
          {list.isLoading ? (
            <div className="text-fg-muted text-sm">Загрузка…</div>
          ) : !list.data || list.data.items.length === 0 ? (
            <div className="text-fg-muted text-sm py-8 text-center">
              Нет профилей. Нажмите «Выпустить VPN-конфиг» — поиск по сотрудникам.
            </div>
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH>Сотрудник</TH>
                  <TH>vpn_email</TH>
                  <TH>Статус</TH>
                  <TH>Создан</TH>
                  <TH className="text-right">Действия</TH>
                </TR>
              </THead>
              <TBody>
                {list.data.items.map((p) => {
                  const st = STATUS_LABEL[p.status] ?? { tone: "neutral", text: p.status };
                  return (
                    <TR key={p.id} className="hover:bg-white/[0.02]">
                      <TD>
                        <div className="font-medium">
                          {p.employee_full_name ?? "—"}
                        </div>
                        <div className="text-xs text-fg-subtle">
                          {p.employee_email ?? p.employee_id}
                        </div>
                      </TD>
                      <TD className="font-mono text-xs">{p.vpn_email}</TD>
                      <TD>
                        <Badge tone={st.tone}>{st.text}</Badge>
                      </TD>
                      <TD className="text-fg-muted text-xs">
                        {new Date(p.created_at).toLocaleString()}
                      </TD>
                      <TD className="text-right">
                        <div className="inline-flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => setDetailId(p.id)}
                            title="Показать sub URL + QR"
                            aria-label="Показать подписку"
                            className="inline-flex items-center justify-center h-8 w-8 rounded-lg border border-border text-fg-muted hover:text-fg hover:bg-white/5 transition-colors"
                          >
                            <QrCode size={14} />
                          </button>
                          {p.status === "active" && (
                            <button
                              type="button"
                              onClick={() => setRevokeTarget(p)}
                              title="Отозвать профиль"
                              aria-label="Отозвать профиль"
                              className="inline-flex items-center justify-center h-8 w-8 rounded-lg border border-border text-state-danger hover:bg-state-danger/10 transition-colors"
                            >
                              <ShieldOff size={14} />
                            </button>
                          )}
                        </div>
                      </TD>
                    </TR>
                  );
                })}
              </TBody>
            </Table>
          )}
        </CardBody>
      </Card>

      <CreateProfileDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(p) => {
          setCreateOpen(false);
          setDetailId(p.id);
        }}
      />

      <RevokeDialog
        target={revokeTarget}
        onClose={() => setRevokeTarget(null)}
      />

      <SubscriptionDialog
        profileId={detailId}
        onClose={() => setDetailId(null)}
      />
    </div>
  );
}

// ─── Create profile ──────────────────────────────────────────────────────

function CreateProfileDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (p: VpnProfileWithSubscription) => void;
}) {
  const [search, setSearch] = useState("");
  const [picked, setPicked] = useState<EmployeeRead | null>(null);
  const employees = useEmployees(search);
  const create = useCreateVpnProfile();
  const [err, setErr] = useState<string | null>(null);

  const submit = async () => {
    if (!picked) return;
    setErr(null);
    try {
      const p = await create.mutateAsync({ employee_id: picked.employee_id });
      toast.success(`Профиль создан: ${p.vpn_email}`);
      setSearch("");
      setPicked(null);
      onCreated(p);
    } catch (e: unknown) {
      const detail =
        (e as { response?: { data?: { detail?: string } } }).response?.data
          ?.detail ?? "Ошибка создания профиля";
      setErr(detail);
      toast.error(detail);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => {
        setPicked(null);
        setSearch("");
        setErr(null);
        onClose();
      }}
      title="Выпустить VPN-конфиг"
    >
      <div className="space-y-4">
        <div className="text-sm text-fg-muted">
          Сотрудник должен сначала залогиниться в NET через signaris-auth — после
          этого появится в списке ниже. Если сотрудника ещё нет — пригласите
          его через{" "}
          <a
            href={`${SIGNARIS_AUTH_URL}/admin/employees`}
            target="_blank"
            rel="noreferrer"
            className="text-accent hover:underline"
          >
            auth.signaris.ru/admin/employees
          </a>
          .
        </div>

        <div>
          <div className="relative">
            <Search
              size={14}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-fg-subtle"
            />
            <Input
              autoFocus
              placeholder="Email или имя сотрудника"
              className="pl-9"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="mt-2 max-h-64 overflow-y-auto border border-border rounded-lg divide-y divide-border">
            {employees.isLoading ? (
              <div className="px-3 py-2 text-sm text-fg-subtle">Поиск…</div>
            ) : !employees.data || employees.data.items.length === 0 ? (
              <div className="px-3 py-2 text-sm text-fg-subtle">
                Никого не найдено.
              </div>
            ) : (
              employees.data.items.map((u) => {
                const active = picked?.employee_id === u.employee_id;
                return (
                  <button
                    key={u.employee_id}
                    type="button"
                    onClick={() => setPicked(u)}
                    className={`w-full px-3 py-2 text-left text-sm flex items-center justify-between hover:bg-white/[0.04] ${active ? "bg-accent-subtle" : ""}`}
                  >
                    <div>
                      <div className="font-medium">{u.full_name}</div>
                      <div className="text-xs text-fg-subtle">{u.email}</div>
                    </div>
                    {active && <Check size={14} className="text-accent" />}
                  </button>
                );
              })
            )}
          </div>
        </div>

        {err && (
          <div className="text-sm text-state-danger flex items-start gap-2">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            {err}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            Отмена
          </Button>
          <Button
            onClick={submit}
            disabled={!picked || create.isPending}
          >
            Выпустить VPN-конфиг
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

// ─── Revoke dialog ───────────────────────────────────────────────────────

function RevokeDialog({
  target,
  onClose,
}: {
  target: VpnProfileRead | null;
  onClose: () => void;
}) {
  const [reason, setReason] = useState("");
  const revoke = useRevokeVpnProfile();
  const [err, setErr] = useState<string | null>(null);

  const submit = async () => {
    if (!target) return;
    setErr(null);
    try {
      await revoke.mutateAsync({ id: target.id, reason: reason || undefined });
      toast.success(`Профиль ${target.vpn_email} отозван`);
      setReason("");
      onClose();
    } catch (e: unknown) {
      const detail =
        (e as { response?: { data?: { detail?: string } } }).response?.data
          ?.detail ?? "Ошибка";
      setErr(detail);
      toast.error(detail);
    }
  };

  return (
    <Dialog
      open={target != null}
      onClose={() => {
        setReason("");
        setErr(null);
        onClose();
      }}
      title={target ? `Отозвать профиль ${target.vpn_email}?` : ""}
    >
      <div className="space-y-3">
        <div className="text-sm text-fg-muted">
          Профиль будет деактивирован в 3X-UI и в локальной БД. Сотрудник
          получит письмо. Действие необратимо.
        </div>
        <div>
          <Input
            placeholder="Причина (необязательно)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>
        {err && <div className="text-sm text-state-danger">{err}</div>}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            Отмена
          </Button>
          <Button
            variant="danger"
            onClick={submit}
            disabled={revoke.isPending}
          >
            <ShieldOff size={14} /> Отозвать
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

// ─── Subscription detail (sub URL + QR) ──────────────────────────────────

function SubscriptionDialog({
  profileId,
  onClose,
}: {
  profileId: string | null;
  onClose: () => void;
}) {
  const detail = useVpnProfileDetail(profileId);
  const [copied, setCopied] = useState(false);

  const copy = async (s: string) => {
    await navigator.clipboard.writeText(s);
    toast.success("Ссылка скопирована");
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <Dialog
      open={profileId != null}
      onClose={() => {
        setCopied(false);
        onClose();
      }}
      title="VPN-подписка"
    >
      {detail.isLoading || !detail.data ? (
        <div className="text-sm text-fg-muted">Загрузка…</div>
      ) : (
        <SubscriptionContent data={detail.data} copied={copied} onCopy={copy} />
      )}
    </Dialog>
  );
}

function SubscriptionContent({
  data,
  copied,
  onCopy,
}: {
  data: VpnProfileWithSubscription;
  copied: boolean;
  onCopy: (s: string) => void;
}) {
  return (
    <div className="space-y-4">
      <div className="text-sm">
        <div className="text-fg-subtle">Сотрудник</div>
        <div className="font-medium">{data.employee_full_name ?? data.employee_id}</div>
        <div className="text-xs text-fg-subtle">{data.employee_email}</div>
      </div>
      <div className="text-sm">
        <div className="text-fg-subtle">vpn_email (technical)</div>
        <div className="font-mono text-xs">{data.vpn_email}</div>
      </div>
      <div>
        <div className="text-sm text-fg-subtle mb-1">Subscription URL</div>
        <div className="flex gap-2">
          <Input
            readOnly
            value={data.subscription_url}
            className="font-mono text-xs"
          />
          <Button
            variant="outline"
            size="sm"
            onClick={() => onCopy(data.subscription_url)}
          >
            {copied ? <Check size={12} /> : <Copy size={12} />}
          </Button>
        </div>
      </div>
      {data.qr_base64 && (
        <div>
          <div className="text-sm text-fg-subtle mb-2">QR (открыть в V2Box)</div>
          <div className="inline-block p-3 bg-white rounded-xl">
            <img src={data.qr_base64} alt="QR" className="w-48 h-48 block" />
          </div>
        </div>
      )}
    </div>
  );
}
